import { useMutation } from '@tanstack/react-query';
import * as Yup from 'yup';
import useAwaitedForm from '../useAwaitedForm';
import {
  updateGiftCardBalance,
  invalidateGiftCardsPage,
  invalidateGiftCard,
} from '../../services/gift-cards';
import { invalidateCustomerOverview } from '../../services/customers';
import { GiftCard } from '../../types/gift-cards';
import { formatDollarAmount } from '../../utils/global/helper';
import { ColoredAlertProps } from '../../components/global/ColoredAlert';

interface Options {
  onSuccess?: () => void | Promise<void>;
  onError?: (error: string) => void | Promise<void>;
}

const validationSchema = Yup.object({
  currentBalance: Yup.string().required('Current balance is required'),
});

function useClearGiftCard({ onSuccess, onError }: Options = {}) {
  const asyncForm = useAwaitedForm();

  return useMutation({
    mutationFn: async (
      giftCard: Pick<GiftCard, 'id' | 'currentBalance' | 'customerId'>,
    ) => {
      const currentBalanceFormatted = formatDollarAmount(
        giftCard.currentBalance / 100,
        true,
      );

      const formResponse = await asyncForm({
        alert: {
          type: 'info',
          subtitle: `This action can't be undone.\nTo confirm this request, please enter the current gift card's remaining balance (${currentBalanceFormatted}) in the field below:`,
        } as ColoredAlertProps,
        form: {
          title: 'Clear gift card balance',
          initialValues: {
            currentBalance: '',
          },
          inputRows: [
            [
              {
                inputType: 'text',
                label: 'Current Balance',
                name: 'currentBalance',
                placeholder: '$0.00',
                required: true,
              },
            ],
          ],
          validationSchema,
          submitButtonText: 'Clear',
        },
      });

      if (!formResponse?.currentBalance) {
        return null;
      }

      // Validate that entered balance matches current balance
      const entered = parseFloat(
        formResponse.currentBalance.replace(/[$,]/g, ''),
      );
      const expected = giftCard.currentBalance / 100;
      if (Math.abs(entered - expected) >= 0.01) {
        throw new Error('BALANCE_MISMATCH');
      }

      await updateGiftCardBalance(giftCard.id, 0);

      return { giftCardId: giftCard.id };
    },
    onSuccess: async (result, giftCard) => {
      if (result) {
        await invalidateGiftCardsPage('invalidate');
        await invalidateGiftCard(giftCard.id);
        if (giftCard.customerId) {
          await invalidateCustomerOverview(giftCard.customerId);
        }
        await onSuccess?.();
      }
    },
    onError: (error: Error) => {
      onError?.(
        error.message === 'BALANCE_MISMATCH'
          ? 'Balance entered does not match'
          : 'Failed to clear gift card balance',
      );
    },
  });
}

export default useClearGiftCard;
